import React, { useEffect, useMemo, useState } from "react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { usePortfolio } from "@/context/PortfolioContext";

const CATEGORIES = ["machinery", "components", "assemblies", "sheet-metal", "concept"];

function emptyForm() {
  return {
    id: null,
    category: "machinery",
    title_it: "",
    title_en: "",
    challenge_it: "",
    challenge_en: "",
    approach_it: "",
    approach_en: "",
    result_it: "",
    result_en: "",
    year: "",
    coord: "",
    order: 0,
  };
}

function toFormData(form, image) {
  const fd = new FormData();
  Object.entries(form).forEach(([k, v]) => {
    if (k === "id") return;
    fd.append(k, v == null ? "" : String(v));
  });
  if (image) fd.append("image", image, image.name);
  return fd;
}

export default function AdminPortfolioPanel() {
  const { authFetch } = useAdminAuth();
  const { refresh: refreshPublic, imageUrl } = usePortfolio();
  const [state, setState] = useState({ loading: true, visible: false, items: [] });
  const [form, setForm] = useState(emptyForm());
  const [image, setImage] = useState(null);
  const [savingItem, setSavingItem] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [error, setError] = useState("");
  const editing = Boolean(form.id);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await authFetch("/admin/portfolio");
      setState({ loading: false, visible: !!data.visible, items: data.items || [] });
    } catch (err) {
      setError("Errore caricamento portfolio");
      setState({ loading: false, visible: false, items: [] });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleVisibility = async () => {
    setSavingToggle(true);
    try {
      const next = !state.visible;
      await authFetch("/admin/portfolio/settings", {
        method: "put",
        data: { visible: next },
        headers: { "Content-Type": "application/json" },
      });
      setState((s) => ({ ...s, visible: next }));
      refreshPublic();
    } catch {
      setError("Errore aggiornamento visibilità");
    } finally {
      setSavingToggle(false);
    }
  };

  const resetForm = () => { setForm(emptyForm()); setImage(null); };

  const editItem = (it) => {
    setForm({
      id: it.id,
      category: it.category || "machinery",
      title_it: it.title_it || "",
      title_en: it.title_en || "",
      challenge_it: it.challenge_it || "",
      challenge_en: it.challenge_en || "",
      approach_it: it.approach_it || "",
      approach_en: it.approach_en || "",
      result_it: it.result_it || "",
      result_en: it.result_en || "",
      year: it.year || "",
      coord: it.coord || "",
      order: it.order || 0,
    });
    setImage(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveItem = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    setError("");
    try {
      const fd = toFormData(form, image);
      if (editing) {
        await authFetch(`/admin/portfolio/items/${form.id}`, { method: "put", data: fd });
      } else {
        await authFetch("/admin/portfolio/items", { method: "post", data: fd });
      }
      resetForm();
      await load();
      refreshPublic();
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : Array.isArray(raw) ? raw.map(x => x?.msg).filter(Boolean).join(" ") : "Errore salvataggio";
      setError(msg);
    } finally {
      setSavingItem(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Eliminare definitivamente questo progetto?")) return;
    try {
      await authFetch(`/admin/portfolio/items/${id}`, { method: "delete" });
      await load();
      refreshPublic();
    } catch {
      setError("Errore eliminazione");
    }
  };

  const sortedItems = useMemo(() => {
    return [...state.items].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [state.items]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-portfolio-panel" className="mx-auto max-w-[1440px] px-6 md:px-10 pt-12 pb-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tighter leading-[0.94]">
            Portfolio<span className="text-[#0055FF]">.</span>
          </h1>
          <p className="mt-3 font-sans text-sm text-[#86868B] max-w-md">
            Gestisci i progetti mostrati sul sito pubblico. La sezione è nascosta finché non attivi l'interruttore qui sotto.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-3 cursor-pointer" data-testid="portfolio-visibility-toggle">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={state.visible}
              onChange={toggleVisibility}
              disabled={savingToggle}
            />
            <span className="relative w-11 h-6 bg-[#E5E5EA] peer-checked:bg-[#0055FF] rounded-full transition-colors duration-400">
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-400 ${state.visible ? "translate-x-5" : ""}`} />
            </span>
            <span className="font-mono text-[11px] tracking-widest uppercase">
              {state.visible ? "Visibile sul sito" : "Nascosto"}
            </span>
          </label>
        </div>
      </div>

      {error && (
        <div data-testid="admin-portfolio-error" className="mb-6 font-mono text-[11px] tracking-widest uppercase text-red-600">✕ {error}</div>
      )}

      {/* Editor form */}
      <form onSubmit={saveItem} className="border-t border-[#E5E5EA] pt-8" data-testid="admin-portfolio-form">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] mb-2">
              {editing ? "Modifica progetto" : "Nuovo progetto"}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Categoria</label>
                <select className="az-input" value={form.category} onChange={setField("category")} data-testid="pf-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Anno</label>
                <input className="az-input" type="text" value={form.year} onChange={setField("year")} data-testid="pf-year" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Ordine</label>
                <input className="az-input" type="number" value={form.order} onChange={setField("order")} data-testid="pf-order" />
              </div>
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Codice / coord.</label>
                <input className="az-input" type="text" value={form.coord} onChange={setField("coord")} data-testid="pf-coord" />
              </div>
              <div className="col-span-2">
                <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">Immagine {editing && "(lascia vuoto per non cambiare)"}</label>
                <label className="flex items-center justify-between border-b border-[#E5E5EA] py-3 cursor-pointer">
                  <span className="font-sans text-[13px] truncate max-w-[70%]">{image ? image.name : "Scegli file…"}</span>
                  <span className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF]">Browse</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setImage(e.target.files?.[0] || null)} data-testid="pf-image" />
                </label>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-4 space-y-6">
            <div className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">Testo — IT</div>
            <TextField label="Titolo IT" value={form.title_it} onChange={setField("title_it")} required testid="pf-title-it" />
            <TextArea label="Sfida IT" value={form.challenge_it} onChange={setField("challenge_it")} testid="pf-challenge-it" />
            <TextArea label="Approccio IT" value={form.approach_it} onChange={setField("approach_it")} testid="pf-approach-it" />
            <TextArea label="Risultato IT" value={form.result_it} onChange={setField("result_it")} testid="pf-result-it" />
          </div>

          <div className="col-span-12 md:col-span-4 space-y-6">
            <div className="font-mono text-[10px] tracking-widest uppercase text-[#86868B]">Text — EN</div>
            <TextField label="Title EN" value={form.title_en} onChange={setField("title_en")} required testid="pf-title-en" />
            <TextArea label="Challenge EN" value={form.challenge_en} onChange={setField("challenge_en")} testid="pf-challenge-en" />
            <TextArea label="Approach EN" value={form.approach_en} onChange={setField("approach_en")} testid="pf-approach-en" />
            <TextArea label="Result EN" value={form.result_en} onChange={setField("result_en")} testid="pf-result-en" />
          </div>
        </div>

        <div className="mt-8 flex items-center flex-wrap gap-4">
          <button type="submit" disabled={savingItem} className="az-btn" data-testid="pf-save">
            <span>{savingItem ? "Salvataggio…" : (editing ? "Salva modifiche" : "Aggiungi progetto")}</span>
            <span className="az-btn__arrow" aria-hidden="true">→</span>
          </button>
          {editing && (
            <button type="button" onClick={resetForm} className="az-btn az-btn--ghost" data-testid="pf-cancel">
              <span>Annulla</span>
            </button>
          )}
        </div>
      </form>

      {/* Existing items list */}
      <div className="mt-16 border-t border-[#E5E5EA]">
        <div className="hidden md:grid grid-cols-12 gap-4 py-3 font-mono text-[10px] tracking-widest uppercase text-[#86868B] border-b border-[#E5E5EA]">
          <div className="col-span-1">#</div>
          <div className="col-span-2">Preview</div>
          <div className="col-span-3">Titolo IT</div>
          <div className="col-span-2">Categoria</div>
          <div className="col-span-1">Anno</div>
          <div className="col-span-1">Ordine</div>
          <div className="col-span-2 text-right">Azioni</div>
        </div>

        {state.loading && (
          <div className="py-12 text-center font-mono text-[11px] tracking-widest uppercase text-[#86868B]">Caricamento…</div>
        )}

        {!state.loading && sortedItems.length === 0 && (
          <div className="py-12 text-center font-mono text-[11px] tracking-widest uppercase text-[#86868B]" data-testid="pf-empty">
            Nessun progetto ancora. Aggiungi il primo qui sopra.
          </div>
        )}

        {sortedItems.map((it, i) => (
          <div
            key={it.id}
            data-testid={`pf-row-${it.id}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 py-5 md:py-6 border-b border-[#E5E5EA] items-center"
          >
            <div className="col-span-1 font-mono text-[11px] tracking-widest text-[#86868B]">{String(i + 1).padStart(2, "0")}</div>
            <div className="col-span-2">
              <div className="relative aspect-[16/10] bg-[#0A0A0A] overflow-hidden">
                {it.image_file_id ? (
                  <img src={imageUrl(it.id)} alt={it.title_it || it.title_en} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40 font-mono text-[9px] tracking-widest uppercase">
                    No image
                  </div>
                )}
              </div>
            </div>
            <div className="col-span-3 font-serif text-lg tracking-tight">{it.title_it || "—"}</div>
            <div className="col-span-2 font-sans text-sm">{it.category}</div>
            <div className="col-span-1 font-mono text-[11px] tracking-widest text-[#86868B]">{it.year || "—"}</div>
            <div className="col-span-1 font-mono text-[11px] tracking-widest text-[#86868B]">{it.order}</div>
            <div className="col-span-2 flex md:justify-end gap-3">
              <button onClick={() => editItem(it)} data-testid={`pf-edit-${it.id}`} className="font-mono text-[10px] tracking-widest uppercase text-[#0055FF]">Modifica</button>
              <button onClick={() => deleteItem(it.id)} data-testid={`pf-delete-${it.id}`} className="font-mono text-[10px] tracking-widest uppercase text-red-600">Elim.</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, required, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
        {label}{required && " *"}
      </label>
      <input type="text" className="az-input" value={value} onChange={onChange} required={required} data-testid={testid} />
    </div>
  );
}

function TextArea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest uppercase text-[#86868B] block mb-2">
        {label}
      </label>
      <textarea rows={3} className="az-input az-input--textarea" value={value} onChange={onChange} data-testid={testid} />
    </div>
  );
}
